import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";

export interface AgentFolder {
  id: string;
  userId: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  workspaceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InsertAgentFolder {
  name: string;
  parentId?: string | null;
  sortOrder?: number;
}

export interface UpdateAgentFolder {
  name?: string;
  parentId?: string | null;
  sortOrder?: number;
}

export interface AgentFolderTreeNode extends AgentFolder {
  children: AgentFolderTreeNode[];
  agentCount: number;
}

class AgentFoldersService {
  private generateId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 9);
    return `agent_folder_${timestamp}${randomPart}`;
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated. Please log in.");
    }
    return session.user.id;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapToFolder(row: any): AgentFolder {
    return {
      id: row.id,
      userId: row.userId || row.user_id,
      name: row.name,
      parentId: row.parentId ?? row.parent_id ?? null,
      sortOrder: row.sortOrder ?? row.sort_order ?? 0,
      workspaceId: row.workspaceId ?? row.workspace_id ?? null,
      createdAt: row.createdAt || row.created_at,
      updatedAt: row.updatedAt || row.updated_at,
    };
  }

  async getFolders(): Promise<AgentFolder[]> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("agent_folders")
      .select("*")
      .eq("user_id", userId)
      .order("sort_order", { ascending: true });

    if (error) {
      if (error.code === "PGRST205" || error.message.includes("does not exist") || error.code === "42P01") {
        console.warn("agent_folders table not yet created.");
        return [];
      }
      throw new Error(`Failed to fetch agent folders: ${error.message}`);
    }

    return (data || []).map((row) => this.mapToFolder(row));
  }

  async getFolder(id: string): Promise<AgentFolder | null> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("agent_folders")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      if (error.code === "PGRST116") return null;
      throw new Error(`Failed to fetch agent folder: ${error.message}`);
    }

    return data ? this.mapToFolder(data) : null;
  }

  async createFolder(folderData: InsertAgentFolder): Promise<AgentFolder> {
    const userId = await this.getCurrentUserId();

    const folderToInsert = {
      id: this.generateId(),
      user_id: userId,
      name: folderData.name,
      parent_id: folderData.parentId ?? null,
      sort_order: folderData.sortOrder ?? 0,
    };

    const { data, error } = await supabase
      .from("agent_folders")
      .insert(folderToInsert)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create agent folder: ${error.message}`);
    }

    return this.mapToFolder(data);
  }

  async updateFolder(id: string, updates: UpdateAgentFolder): Promise<AgentFolder> {
    const userId = await this.getCurrentUserId();

    const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.parentId !== undefined) updateData.parent_id = updates.parentId;
    if (updates.sortOrder !== undefined) updateData.sort_order = updates.sortOrder;

    const { data, error } = await supabase
      .from("agent_folders")
      .update(updateData)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update agent folder: ${error.message}`);
    }

    return this.mapToFolder(data);
  }

  async deleteFolder(id: string): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("agent_folders")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      throw new Error(`Failed to delete agent folder: ${error.message}`);
    }
  }

  async moveAgentToFolder(agentId: string, folderId: string | null): Promise<void> {
    const userId = await this.getCurrentUserId();

    if (folderId) {
      const folder = await this.getFolder(folderId);
      if (!folder) {
        throw new Error("Folder not found");
      }
    }

    const { error } = await supabase
      .from("ai_agents")
      .update({ folder_id: folderId, updated_at: new Date().toISOString() })
      .eq("id", agentId)
      .eq("user_id", userId);

    if (error) {
      throw new Error(`Failed to move agent to folder: ${error.message}`);
    }
  }

  async getAgentsInFolder(folderId: string): Promise<{ id: string; name: string }[]> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("ai_agents")
      .select("id, name")
      .eq("user_id", userId)
      .eq("folder_id", folderId);

    if (error) {
      throw new Error(`Failed to fetch agents in folder: ${error.message}`);
    }

    return data || [];
  }

  async getAgentCountByFolder(): Promise<Record<string, number>> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("ai_agents")
      .select("folder_id")
      .eq("user_id", userId)
      .not("folder_id", "is", null);

    if (error) {
      return {};
    }

    const counts: Record<string, number> = {};
    for (const row of data || []) {
      const r = row as { folder_id?: string; folderId?: string };
      const fid = r.folder_id ?? r.folderId;
      if (fid) counts[fid] = (counts[fid] || 0) + 1;
    }
    return counts;
  }

  buildFolderTree(folders: AgentFolder[], agentCounts: Record<string, number>): AgentFolderTreeNode[] {
    const folderMap = new Map<string, AgentFolderTreeNode>();
    for (const folder of folders) {
      folderMap.set(folder.id, {
        ...folder,
        children: [],
        agentCount: agentCounts[folder.id] || 0,
      });
    }

    const rootNodes: AgentFolderTreeNode[] = [];
    for (const node of folderMap.values()) {
      if (node.parentId && folderMap.has(node.parentId)) {
        folderMap.get(node.parentId)!.children.push(node);
      } else if (!node.parentId || !folderMap.has(node.parentId)) {
        rootNodes.push(node);
      }
    }

    const sortNodes = (nodes: AgentFolderTreeNode[]) => {
      nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
      for (const node of nodes) {
        if (node.children.length > 0) sortNodes(node.children);
      }
    };

    sortNodes(rootNodes);
    return rootNodes;
  }

  async getFolderTree(): Promise<AgentFolderTreeNode[]> {
    const folders = await this.getFolders();
    const agentCounts = await this.getAgentCountByFolder();
    return this.buildFolderTree(folders, agentCounts);
  }

  async findOrCreateFolderByName(name: string, parentId: string | null = null): Promise<AgentFolder> {
    const folders = await this.getFolders();
    const existing = folders.find(
      (f) => f.name === name && (f.parentId ?? null) === (parentId ?? null)
    );
    if (existing) return existing;
    return this.createFolder({ name, parentId });
  }
}

export const agentFoldersService = new AgentFoldersService();
